import type { DictFragment } from '../translations';

/** Admin page: tabs (Users/Roles/Permissions/Integrations/Naming/Personalisation), dev tools, and the auth-sso-kit UserManagementPanel label overrides. */
export const admin: DictFragment = {
  fr: {
    // ── tabs ──
    'admin.tab.users': 'Utilisateurs',
    'admin.tab.roles': 'Rôles',
    'admin.tab.permissions': 'Autorisations',
    'admin.tab.integrations': 'Intégrations',
    'admin.tab.naming': 'Nommage',
    'admin.tab.personalisation': 'Personnalisation',

    // ── dev-only: reset dev from prod ──
    'admin.devReset.title': 'Données de dev — Réinitialiser depuis la production',
    'admin.devReset.descriptionPrefix': 'Efface cette base de données de',
    'admin.devReset.descriptionSuffix': 'et la remplace par une copie récente de la production, puis réapplique les migrations de dev. La production est en lecture seule et n’est jamais modifiée. Cette commande n’existe qu’en dev.',
    'admin.devReset.button': 'Réinitialiser dev depuis prod',
    'admin.devReset.lastReset': 'Dernière réinitialisation : {date}',
    'admin.devReset.successAlert': 'Base de données de dev réinitialisée depuis prod. Rechargement…',
    'admin.devReset.failedAlert': 'Échec de la réinitialisation',
    'admin.devReset.modalWarningPart1': 'Cette action va',
    'admin.devReset.modalWarningStrong1': 'effacer définitivement la base de données de dev',
    'admin.devReset.modalWarningPart2': 'et la remplacer par une copie fraîche de la',
    'admin.devReset.modalWarningStrong2': 'production',
    'admin.devReset.modalWarningPart3': ', puis réappliquer les migrations de dev. Toute donnée de test propre à dev sera perdue. La production est accédée en lecture seule et n’est pas affectée.',
    'admin.devReset.typePrefix': 'Tapez',
    'admin.devReset.typeSuffix': 'pour confirmer',
    'admin.devReset.resettingLabel': 'Réinitialisation…',
    'admin.devReset.confirmButton': 'Réinitialiser dev',

    // ── dev-only: apply stage-model migration ──
    'admin.migration.title': 'Données de dev — Appliquer la migration du modèle de phases',
    'admin.migration.descriptionPrefix': 'Convertit cette base de données de',
    'admin.migration.descriptionSuffix': 'vers le nouveau cycle de vie de projet (Inbox → Préqualification → Scoping → Qualification → Planning → Execution → Handover → Closed, plus Backlog/Deferred) et remappe les projets existants (Inbox/Execution/Done conservés ; tout le reste → Deferred). Idempotent et sûr à relancer. La production n’est jamais touchée.',
    'admin.migration.button': 'Appliquer la migration des phases',
    'admin.migration.failedAlert': 'Échec de la migration : {message}',
    'admin.migration.stagesNowLabel': 'Phases actuelles :',
    'admin.migration.projectsPerStageLabel': 'Projets par phase :',
    'admin.migration.noProjects': 'Aucun projet.',

    // ── shared across tabs ──
    'admin.lastLogin': 'Dernière connexion',
    'admin.never': 'Jamais',
    'admin.colUser': 'Utilisateur',
    'admin.colRole': 'Rôle',
    'admin.selectRolePlaceholder': 'Sélectionner un rôle',
    'admin.role.admin': 'Admin',
    'admin.role.direction': 'Direction',
    'admin.role.user': 'Utilisateur',
    'admin.roleUpdateFailedAlert': 'Échec de la mise à jour du rôle',

    // ── roles tab ──
    'admin.roles.title': 'Rôles des utilisateurs',
    'admin.roles.description': 'Attribuez des rôles pour contrôler les accès. Admin a un accès complet, Direction peut tout consulter mais ne peut modifier que les projets qu’il commandite, User a un accès limité selon son affectation aux projets.',

    // ── permissions tab ──
    'admin.permissions.title': 'Vue d’ensemble des autorisations',
    'admin.permissions.description': 'Aperçu en lecture seule de ce que chaque rôle peut faire. Les rôles s’attribuent dans l’onglet Rôles.',
    'admin.permissions.colCapability': 'Fonctionnalité',

    // ── integrations: shared ──
    'admin.integrations.connected': 'Connecté',
    'admin.integrations.configured': 'Configuré',
    'admin.integrations.statusLabel': 'Statut',
    'admin.integrations.baseUrlLabel': 'URL de base',

    // ── integrations: calendar ──
    'admin.integrations.calendar.title': 'Intégration calendrier (Microsoft 365)',
    'admin.integrations.calendar.description': 'Connectez-vous à Microsoft 365 pour synchroniser les événements du calendrier Outlook avec les projets.',
    'admin.integrations.calendar.registerPrefix': 'Enregistrez une application dans Azure AD avec la permission d’application',
    'admin.integrations.calendar.registerSuffix': 'et accordez le consentement administrateur.',
    'admin.integrations.calendar.tenantId': 'ID du locataire (Tenant ID)',
    'admin.integrations.calendar.clientId': 'ID client (Client ID)',
    'admin.integrations.calendar.clientSecret': 'Secret client',
    'admin.integrations.calendar.clientSecretPlaceholder': 'Entrez le secret…',
    'admin.integrations.calendar.webhookUrl': 'URL du webhook',
    'admin.integrations.calendar.webhookHelp': 'Doit être accessible publiquement en HTTPS.',
    'admin.integrations.calendar.syncStatusLabel': 'État de la synchronisation',
    'admin.integrations.calendar.syncStatusText': '{syncedUsers} utilisateur(s) inscrit(s), {activeSubs} abonnement(s) actif(s), {totalEvents} événement(s) synchronisé(s)',
    'admin.integrations.calendar.forceFullSync': 'Forcer une synchronisation complète',
    'admin.integrations.calendar.syncedAlert': '{totalEvents} événement(s) synchronisé(s) pour {usersSynced} utilisateur(s)',
    'admin.integrations.calendar.forceRematchAll': 'Forcer le réappariement de tous les événements',
    'admin.integrations.calendar.rematchedAlert': '{count} événement(s) réapparié(s)',

    // ── integrations: speakr ──
    'admin.integrations.speakr.title': 'Intégration Speakr (Transcriptions)',
    'admin.integrations.speakr.description': 'Connectez-vous à une instance Speakr auto-hébergée pour récupérer les transcriptions de réunions. Chaque utilisateur configure son propre jeton d’API dans son profil. Définissez l’URL de base ici pour qu’elle soit pré-remplie pour tout le monde.',
    'admin.integrations.speakr.baseUrl': 'URL de base Speakr',
    'admin.integrations.speakr.statusText': '{configuredUsers} utilisateur(s) avec un jeton, {totalTranscriptions} transcription(s) synchronisée(s)',
    'admin.integrations.speakr.syncAllUsers': 'Synchroniser tous les utilisateurs',
    'admin.integrations.speakr.syncedAlert': '{totalSynced} transcription(s) synchronisée(s) depuis {usersSynced} utilisateur(s)',
    'admin.integrations.speakr.rematchTranscriptions': 'Réapparier les transcriptions',
    'admin.integrations.speakr.rematchedAlert': '{count} transcription(s) réappariée(s)',
    'admin.integrations.speakr.backfillTranscripts': 'Compléter les transcriptions manquantes',
    'admin.integrations.speakr.backfilledAlert': '{count} transcription(s) complétée(s)',
    'admin.integrations.speakr.testDataLabel': 'Données de test',
    'admin.integrations.speakr.testDataDescription': 'Générez ou supprimez des événements de calendrier factices pour tester la chronologie visuellement.',
    'admin.integrations.speakr.seedDummyEvents': 'Générer des événements factices',
    'admin.integrations.speakr.seededAlert': '{count} événement(s) factice(s) généré(s)',
    'admin.integrations.speakr.clearDummyEvents': 'Supprimer les événements factices',
    'admin.integrations.speakr.deletedAlert': '{count} événement(s) factice(s) supprimé(s)',

    // ── integrations: slack ──
    'admin.integrations.slack.title': 'Intégration Slack',
    'admin.integrations.slack.description': 'Connectez-vous à Slack pour transmettre les mises à jour de la chronologie (commentaires, réponses) vers des canaux de projet dédiés.',
    'admin.integrations.slack.scopesPrefix': 'Créez une application Slack avec les scopes de bot suivants :',
    'admin.integrations.slack.botToken': 'Jeton du bot',
    'admin.integrations.slack.signingSecret': 'Secret de signature',
    'admin.integrations.slack.signingSecretPlaceholder': 'Entrez le secret de signature…',
    'admin.integrations.slack.enableForNewProjects': 'Activer pour les nouveaux projets',
    'admin.integrations.slack.statusText': '{channelsCreated} canal/canaux créé(s), {mappedUsers} utilisateur(s) associé(s)',
    'admin.integrations.slack.createChannelsButton': 'Créer les canaux pour tous les projets',
    'admin.integrations.slack.createChannelsAlert': '{created} canal/canaux créé(s), {failed} échec(s) sur {total} projets',

    // ── integrations: pistache (AI / LLM) ──
    'admin.integrations.pistache.title': 'Pistache (IA / LLM)',
    'admin.integrations.pistache.description': 'Connectez un point de terminaison LLM compatible OpenAI pour alimenter les suggestions de préqualification (évaluation stratégique et séquencement).',
    'admin.integrations.pistache.apiUrl': 'URL de l’API',
    'admin.integrations.pistache.model': 'Modèle',
    'admin.integrations.pistache.modelPlaceholder': 'p. ex. gpt-4o',
    'admin.integrations.pistache.testRefreshModels': 'Tester / actualiser les modèles',
    'admin.integrations.pistache.fetchModelsFailed': 'Échec de la récupération des modèles',
    'admin.integrations.pistache.availableModels': 'Disponibles : {models}',

    // ── integrations: RCAJO ──
    'admin.integrations.rcajo.title': 'RCAJO (Inventaire des ressources et capacités)',
    'admin.integrations.rcajo.description': 'Connectez-vous au service RCAJO qui détient l’inventaire des ressources et capacités de l’organisation, utilisé pour la vérification stratégique. Communication serveur à serveur ; utilise l’origine de dev par défaut si laissé vide.',
    'admin.integrations.rcajo.hostHeader': 'En-tête Host',
    'admin.integrations.rcajo.apiKey': 'Clé API',
    'admin.integrations.rcajo.apiKeyPlaceholder': 'Entrez la clé API…',
    'admin.integrations.rcajo.testConnection': 'Tester la connexion',
    'admin.integrations.rcajo.connectedText': 'Connecté — {resourceCount} ressources, {capabilityCount} capacités',
    'admin.integrations.rcajo.connectionFailed': 'Échec de la connexion',

    // ── naming tab ──
    'admin.naming.emailPattern.title': 'Modèle d’e-mail',
    'admin.naming.emailPattern.description': 'Génère automatiquement l’adresse e-mail des nouvelles personnes. Jetons : {firstname}, {lastname}',
    'admin.naming.portfolioDomains.title': 'Domaines du portefeuille (colonnes)',
    'admin.naming.portfolioDomains.placeholder': 'Nouveau domaine…',
    'admin.naming.pipelineStages.title': 'Étapes du pipeline (colonnes)',
    'admin.naming.pipelineStages.placeholder': 'Nouvelle étape…',
    'admin.naming.taskBoardColumns.title': 'Colonnes du tableau de tâches',
    'admin.naming.taskBoardColumns.description': 'Définissez les colonnes du tableau de gestion des tâches. L’ordre détermine la position des colonnes (la première = la plus à gauche).',
    'admin.naming.taskBoardColumns.placeholder': 'Nouveau statut…',
    'admin.naming.levels.title': 'Niveaux (lignes)',
    'admin.naming.levels.placeholder': 'Nouveau niveau…',

    // ── personalisation tab ──
    'admin.personalisation.accentColor.title': 'Couleur d’accent',
    'admin.personalisation.logo.title': 'Logo',
    'admin.personalisation.logo.description': 'Téléversez un PNG à fond transparent. Il s’affiche dans la barre supérieure à côté du nom de la marque. S’inverse automatiquement en mode sombre.',
    'admin.personalisation.logo.currentLogoAlt': 'Logo actuel',
    'admin.personalisation.logo.replace': 'Remplacer',
    'admin.personalisation.logo.upload': 'Téléverser le logo',
    'admin.personalisation.logo.onlyPngAlert': 'Seuls les fichiers PNG sont acceptés',
    'admin.personalisation.logo.fileTooLargeAlert': 'Fichier trop volumineux (max 1,5 Mo)',

    // ── reset-from-prod confirm modal ──
    'admin.resetModal.title': 'Réinitialiser dev depuis prod',

    // ── UserManagementPanel label overrides (auth-sso-kit) ──
    'admin.regModeTitle': 'Mode d’inscription',
    'admin.regModeOpen': 'Ouvert',
    'admin.regModeAllowlist': 'Liste blanche',
    'admin.regModeOpenHelp': 'Toute personne avec une adresse e-mail valide peut s’inscrire. Le premier utilisateur devient admin.',
    'admin.regModeAllowlistHelp': 'Seules les adresses e-mail pré-approuvées peuvent se connecter.',
    'admin.allowlistTitle': 'Liste blanche d’e-mails',
    'admin.allowlistNotEnforced': 'La liste blanche n’est pas actuellement appliquée. Passez en mode Liste blanche pour restreindre l’inscription.',
    'admin.allowlistHelpEnforced': 'Seules ces adresses e-mail peuvent se connecter. Ajoutez les e-mails avant que les utilisateurs ne s’authentifient.',
    'admin.allowlistHelpNotEnforced': 'Gérez les e-mails pour le jour où vous passerez en mode Liste blanche.',
    'admin.emailPlaceholder': 'email@exemple.com',
    'admin.namePlaceholder': 'Nom (facultatif)',
    'admin.addToAllowlist': 'Ajouter à la liste blanche',
    'admin.createAccount': 'Créer le compte',
    'admin.errValidEmailRequired': 'Une adresse e-mail valide est requise',
    'admin.colAdded': 'Ajouté',
    'admin.statusRegistered': 'Inscrit ({name})',
    'admin.statusPending': 'En attente',
    'admin.impersonate': 'Emprunter l’identité',
    'admin.confirmRemoveEmail': 'Retirer « {email} » de la liste blanche ?',
    'admin.userMgmtTitle': 'Gestion des utilisateurs',
    'admin.colJoined': 'Inscrit le',
    'admin.badgeAdmin': 'Admin',
    'admin.badgeUser': 'Utilisateur',
    'admin.removeAdmin': 'Retirer le rôle admin',
    'admin.makeAdmin': 'Nommer admin',
    'admin.rolesButton': 'Rôles',
    'admin.resetPassword': 'Réinitialiser le mot de passe',
    'admin.confirmDeleteUser': 'Supprimer l’utilisateur « {name} » ? Cela supprimera également toutes ses données.',
    'admin.newPasswordPlaceholder': 'Nouveau mot de passe (8 caractères min.)',
    'admin.setButton': 'Définir',
    'admin.saveRoles': 'Enregistrer les rôles',
    'admin.errPasswordMin': 'Le mot de passe doit contenir au moins 8 caractères',
    'admin.passwordResetDone': 'Mot de passe réinitialisé avec succès',
  },
  en: {
    // ── tabs ──
    'admin.tab.users': 'Users',
    'admin.tab.roles': 'Roles',
    'admin.tab.permissions': 'Permissions',
    'admin.tab.integrations': 'Integrations',
    'admin.tab.naming': 'Naming',
    'admin.tab.personalisation': 'Personalisation',

    // ── dev-only: reset dev from prod ──
    'admin.devReset.title': 'Dev Data — Reset from Production',
    'admin.devReset.descriptionPrefix': 'Wipe this',
    'admin.devReset.descriptionSuffix': 'database and replace it with a current copy of production, then re-apply dev migrations. Production is read-only and is never modified. This control only exists on dev.',
    'admin.devReset.button': 'Reset dev from prod',
    'admin.devReset.lastReset': 'Last reset: {date}',
    'admin.devReset.successAlert': 'Dev database reset from prod. Reloading…',
    'admin.devReset.failedAlert': 'Refresh failed',
    'admin.devReset.modalWarningPart1': 'This will',
    'admin.devReset.modalWarningStrong1': 'permanently wipe the dev database',
    'admin.devReset.modalWarningPart2': 'and replace it with a fresh copy of',
    'admin.devReset.modalWarningStrong2': 'production',
    'admin.devReset.modalWarningPart3': ', then re-apply dev migrations. Any dev-only test data will be lost. Production is accessed read-only and is not affected.',
    'admin.devReset.typePrefix': 'Type',
    'admin.devReset.typeSuffix': 'to confirm',
    'admin.devReset.resettingLabel': 'Resetting…',
    'admin.devReset.confirmButton': 'Reset dev',

    // ── dev-only: apply stage-model migration ──
    'admin.migration.title': 'Dev Data — Apply stage-model migration',
    'admin.migration.descriptionPrefix': 'Convert this',
    'admin.migration.descriptionSuffix': 'database to the new project lifecycle (Inbox → Préqualification → Scoping → Qualification → Planning → Execution → Handover → Closed, plus Backlog/Deferred) and re-map existing projects (Inbox/Execution/Done kept; everything else → Deferred). Idempotent and run-once-safe. Production is never touched.',
    'admin.migration.button': 'Apply stage migration',
    'admin.migration.failedAlert': 'Migration failed: {message}',
    'admin.migration.stagesNowLabel': 'Stages now:',
    'admin.migration.projectsPerStageLabel': 'Projects per stage:',
    'admin.migration.noProjects': 'No projects.',

    // ── shared across tabs ──
    'admin.lastLogin': 'Last Login',
    'admin.never': 'Never',
    'admin.colUser': 'User',
    'admin.colRole': 'Role',
    'admin.selectRolePlaceholder': 'Select role',
    'admin.role.admin': 'Admin',
    'admin.role.direction': 'Direction',
    'admin.role.user': 'User',
    'admin.roleUpdateFailedAlert': 'Failed to update role',

    // ── roles tab ──
    'admin.roles.title': 'User Roles',
    'admin.roles.description': 'Assign roles to control access. Admin has full access, Direction can view everything but only modify sponsored projects, User has limited access based on project assignment.',

    // ── permissions tab ──
    'admin.permissions.title': 'Permissions Overview',
    'admin.permissions.description': 'Read-only overview of what each role can do. Roles are assigned in the Roles tab.',
    'admin.permissions.colCapability': 'Capability',

    // ── integrations: shared ──
    'admin.integrations.connected': 'Connected',
    'admin.integrations.configured': 'Configured',
    'admin.integrations.statusLabel': 'Status',
    'admin.integrations.baseUrlLabel': 'Base URL',

    // ── integrations: calendar ──
    'admin.integrations.calendar.title': 'Calendar Integration (Microsoft 365)',
    'admin.integrations.calendar.description': 'Connect to Microsoft 365 to sync Outlook calendar events to projects.',
    'admin.integrations.calendar.registerPrefix': 'Register an app in Azure AD with',
    'admin.integrations.calendar.registerSuffix': 'application permission and grant admin consent.',
    'admin.integrations.calendar.tenantId': 'Tenant ID',
    'admin.integrations.calendar.clientId': 'Client ID',
    'admin.integrations.calendar.clientSecret': 'Client Secret',
    'admin.integrations.calendar.clientSecretPlaceholder': 'Enter secret...',
    'admin.integrations.calendar.webhookUrl': 'Webhook URL',
    'admin.integrations.calendar.webhookHelp': 'Must be publicly accessible via HTTPS.',
    'admin.integrations.calendar.syncStatusLabel': 'Sync Status',
    'admin.integrations.calendar.syncStatusText': '{syncedUsers} user(s) opted in, {activeSubs} active subscription(s), {totalEvents} event(s) synced',
    'admin.integrations.calendar.forceFullSync': 'Force Full Sync',
    'admin.integrations.calendar.syncedAlert': 'Synced {totalEvents} event(s) for {usersSynced} user(s)',
    'admin.integrations.calendar.forceRematchAll': 'Force Re-match All Events',
    'admin.integrations.calendar.rematchedAlert': 'Re-matched {count} event(s)',

    // ── integrations: speakr ──
    'admin.integrations.speakr.title': 'Speakr Integration (Transcriptions)',
    'admin.integrations.speakr.description': 'Connect to a self-hosted Speakr instance to pull meeting transcriptions. Each user configures their own API token in their profile. Set the base URL here so it’s pre-populated for everyone.',
    'admin.integrations.speakr.baseUrl': 'Speakr Base URL',
    'admin.integrations.speakr.statusText': '{configuredUsers} user(s) with tokens, {totalTranscriptions} transcription(s) synced',
    'admin.integrations.speakr.syncAllUsers': 'Sync All Users',
    'admin.integrations.speakr.syncedAlert': 'Synced {totalSynced} transcription(s) from {usersSynced} user(s)',
    'admin.integrations.speakr.rematchTranscriptions': 'Re-match Transcriptions',
    'admin.integrations.speakr.rematchedAlert': 'Re-matched {count} transcription(s)',
    'admin.integrations.speakr.backfillTranscripts': 'Backfill Transcripts',
    'admin.integrations.speakr.backfilledAlert': 'Backfilled {count} transcript(s)',
    'admin.integrations.speakr.testDataLabel': 'Test Data',
    'admin.integrations.speakr.testDataDescription': 'Seed or remove dummy calendar events to test the timeline visually.',
    'admin.integrations.speakr.seedDummyEvents': 'Seed Dummy Events',
    'admin.integrations.speakr.seededAlert': 'Seeded {count} dummy event(s)',
    'admin.integrations.speakr.clearDummyEvents': 'Clear Dummy Events',
    'admin.integrations.speakr.deletedAlert': 'Deleted {count} dummy event(s)',

    // ── integrations: slack ──
    'admin.integrations.slack.title': 'Slack Integration',
    'admin.integrations.slack.description': 'Connect to Slack to push timeline updates (comments, replies) to dedicated project channels.',
    'admin.integrations.slack.scopesPrefix': 'Create a Slack App with Bot scopes:',
    'admin.integrations.slack.botToken': 'Bot Token',
    'admin.integrations.slack.signingSecret': 'Signing Secret',
    'admin.integrations.slack.signingSecretPlaceholder': 'Enter signing secret...',
    'admin.integrations.slack.enableForNewProjects': 'Enable for new projects',
    'admin.integrations.slack.statusText': '{channelsCreated} channel(s) created, {mappedUsers} user(s) mapped',
    'admin.integrations.slack.createChannelsButton': 'Create Channels for All Projects',
    'admin.integrations.slack.createChannelsAlert': 'Created {created} channel(s), {failed} failed out of {total} projects',

    // ── integrations: pistache (AI / LLM) ──
    'admin.integrations.pistache.title': 'Pistache (AI / LLM)',
    'admin.integrations.pistache.description': 'Connect to an OpenAI-compatible LLM endpoint to power préqualification suggestions (strategic assessment & sequencing).',
    'admin.integrations.pistache.apiUrl': 'API URL',
    'admin.integrations.pistache.model': 'Model',
    'admin.integrations.pistache.modelPlaceholder': 'e.g. gpt-4o',
    'admin.integrations.pistache.testRefreshModels': 'Test / refresh models',
    'admin.integrations.pistache.fetchModelsFailed': 'Failed to fetch models',
    'admin.integrations.pistache.availableModels': 'Available: {models}',

    // ── integrations: RCAJO ──
    'admin.integrations.rcajo.title': 'RCAJO (Resource & Capability Inventory)',
    'admin.integrations.rcajo.description': 'Connect to the RCAJO service holding the organisation’s capability/resource inventory, used for strategic verification. Server-to-server; defaults to the dev origin if left blank.',
    'admin.integrations.rcajo.hostHeader': 'Host header',
    'admin.integrations.rcajo.apiKey': 'API Key',
    'admin.integrations.rcajo.apiKeyPlaceholder': 'Enter API key...',
    'admin.integrations.rcajo.testConnection': 'Test connection',
    'admin.integrations.rcajo.connectedText': 'Connected — {resourceCount} resources, {capabilityCount} capabilities',
    'admin.integrations.rcajo.connectionFailed': 'Connection failed',

    // ── naming tab ──
    'admin.naming.emailPattern.title': 'Email Pattern',
    'admin.naming.emailPattern.description': 'Auto-generates email for new people. Tokens: {firstname}, {lastname}',
    'admin.naming.portfolioDomains.title': 'Portfolio Domains (Columns)',
    'admin.naming.portfolioDomains.placeholder': 'New domain...',
    'admin.naming.pipelineStages.title': 'Pipeline Stages (Columns)',
    'admin.naming.pipelineStages.placeholder': 'New stage...',
    'admin.naming.taskBoardColumns.title': 'Task Board Columns',
    'admin.naming.taskBoardColumns.description': 'Define the columns for the task management board. The order determines column position (first = leftmost).',
    'admin.naming.taskBoardColumns.placeholder': 'New status...',
    'admin.naming.levels.title': 'Levels (Rows)',
    'admin.naming.levels.placeholder': 'New level...',

    // ── personalisation tab ──
    'admin.personalisation.accentColor.title': 'Accent Color',
    'admin.personalisation.logo.title': 'Logo',
    'admin.personalisation.logo.description': 'Upload a PNG with transparent background. Displays in the topbar next to the brand name. Inverts automatically in dark mode.',
    'admin.personalisation.logo.currentLogoAlt': 'Current logo',
    'admin.personalisation.logo.replace': 'Replace',
    'admin.personalisation.logo.upload': 'Upload Logo',
    'admin.personalisation.logo.onlyPngAlert': 'Only PNG files are accepted',
    'admin.personalisation.logo.fileTooLargeAlert': 'File too large (max 1.5MB)',

    // ── reset-from-prod confirm modal ──
    'admin.resetModal.title': 'Reset dev from prod',

    // ── UserManagementPanel label overrides (auth-sso-kit) ──
    'admin.regModeTitle': 'Registration Mode',
    'admin.regModeOpen': 'Open',
    'admin.regModeAllowlist': 'Allowlist',
    'admin.regModeOpenHelp': 'Anyone with a valid email can register. The first user becomes admin.',
    'admin.regModeAllowlistHelp': 'Only pre-approved emails can log in.',
    'admin.allowlistTitle': 'Email Allowlist',
    'admin.allowlistNotEnforced': 'The allowlist is not currently enforced. Switch to Allowlist mode to restrict registration.',
    'admin.allowlistHelpEnforced': 'Only these email addresses can log in. Add emails before users authenticate.',
    'admin.allowlistHelpNotEnforced': 'Manage emails for when you switch to Allowlist mode.',
    'admin.emailPlaceholder': 'email@example.com',
    'admin.namePlaceholder': 'Name (optional)',
    'admin.addToAllowlist': 'Add to Allowlist',
    'admin.createAccount': 'Create Account',
    'admin.errValidEmailRequired': 'Valid email is required',
    'admin.colAdded': 'Added',
    'admin.statusRegistered': 'Registered ({name})',
    'admin.statusPending': 'Pending',
    'admin.impersonate': 'Impersonate',
    'admin.confirmRemoveEmail': 'Remove "{email}" from the allowlist?',
    'admin.userMgmtTitle': 'User Management',
    'admin.colJoined': 'Joined',
    'admin.badgeAdmin': 'Admin',
    'admin.badgeUser': 'User',
    'admin.removeAdmin': 'Remove Admin',
    'admin.makeAdmin': 'Make Admin',
    'admin.rolesButton': 'Roles',
    'admin.resetPassword': 'Reset Password',
    'admin.confirmDeleteUser': 'Delete user "{name}"? This will also delete all their data.',
    'admin.newPasswordPlaceholder': 'New password (min 8 chars)',
    'admin.setButton': 'Set',
    'admin.saveRoles': 'Save Roles',
    'admin.errPasswordMin': 'Password must be at least 8 characters',
    'admin.passwordResetDone': 'Password reset successfully',
  },
};
